import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const COLLECTION = "etkinlikKayitlari";

type Durum = "bekliyor" | "onaylandi";

interface Kayit {
  id: string;
  ad: string;
  email: string;
  telefon: string;
  etkinlikBaslik: string;
  etkinlikId: number | null;
  tarih: Timestamp | null;
  durum: Durum;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  bekliyor: { label: "Bekliyor", color: "bg-yellow-100 text-yellow-700" },
  onaylandi: { label: "Onaylandı", color: "bg-green-100 text-green-700" },
};

function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try { return t.toDate().toLocaleDateString("tr-TR"); } catch { return "—"; }
}

const EtkinlikKayitlariPage: React.FC = () => {
  const [rows, setRows] = useState<Kayit[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [filter, setFilter] = useState<string>("hepsi");

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("tarih", "desc")));
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Kayit[]);
    } catch (e) {
      console.error("Etkinlik kayıtları okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => filter === "hepsi" || r.durum === filter);

  const onayla = async (id: string) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), { durum: "onaylandi" });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, durum: "onaylandi" } : r)));
    } catch (e) {
      console.error("Durum güncellenemedi:", e);
      window.alert("Durum güncellenemedi, lütfen tekrar deneyin.");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error("Silinemedi:", e);
      window.alert("Silme başarısız, lütfen tekrar deneyin.");
    }
  };

  const bekleyen = rows.filter((r) => r.durum === "bekliyor").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Etkinlik Kayıtları</h2>
          <p className="text-sm text-gray-500 mt-0.5">{bekleyen} bekleyen, {rows.length} toplam kayıt · web "Katıl" formundan</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["hepsi", "bekliyor", "onaylandi"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${filter === f ? "bg-[#8B1428] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
              {f === "hepsi" ? "Hepsi" : STATUS_LABELS[f]?.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Kayıtlar yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-calendar-check text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz etkinlik kaydı yok</p>
          <p className="text-gray-400 text-sm mt-1">Web sitesinde bir etkinliğe "Katıl" denildiğinde burada görünecek</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ad</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">E-posta</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Telefon</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Etkinlik</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{r.ad}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{r.email}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs hidden md:table-cell">{r.telefon || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{r.etkinlikBaslik || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{formatTarih(r.tarih)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_LABELS[r.durum]?.color ?? "bg-gray-100 text-gray-600"}`}>
                      {STATUS_LABELS[r.durum]?.label ?? r.durum}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.durum === "bekliyor" && (
                        <button onClick={() => onayla(r.id)} className="text-green-600 hover:text-green-800 text-xs font-semibold">Onayla</button>
                      )}
                      <button onClick={() => remove(r.id)} className="text-gray-400 hover:text-red-500 transition-colors"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">{filtered.length} kayıt gösteriliyor</div>
        </div>
      )}
    </div>
  );
};

export default EtkinlikKayitlariPage;
